import { experiments } from '../../../experiments';

// Some MIME types reported by the browser are not on the allowed list in the media platform.
// This map remaps them to their accepted equivalents.
const MIME_TYPE_OVERRIDES: Record<string, string> = {
  'image/x-pentax-pef': 'image/x-raw-pentax',
  'image/x-panasonic-rw2': 'image/x-raw-panasonic',
  'image/x-minolta-mrw': 'image/x-raw-minolta',
};

// MIME fallback for ''/octet-stream files; covers every supported extension (constants.spec.ts).
const EXTENSIONS_BY_MIME: Record<string, string[]> = {
  'image/jpeg': ['jpg', 'jpeg', 'jpe', 'jfif'],
  'image/png': ['png'],
  'image/gif': ['gif'],
  'image/bmp': ['bmp'],
  'image/webp': ['webp'],
  'image/heic': ['heic'],
  'image/heif': ['heif'],
  'image/tiff': ['tiff', 'tif'],
  'image/jp2': ['jp2', 'jpg2', 'j2k', 'jpf', 'jpm', 'j2c', 'jpc', 'jpx'],
  'image/x-raw-sony': ['arw', 'sr2', 'srf'],
  'image/x-raw-samsung': ['srw'],
  'image/x-raw-nikon': ['nef'],
  'image/x-raw-canon': ['cr2', 'cr3', 'crw'],
  'image/x-raw-leica': ['rwl'],
  'image/x-raw-panasonic': ['rw2', 'raw'],
  'image/x-raw-fuji': ['raf'],
  'image/x-raw-pentax': ['pef'],
  'image/x-raw-olympus': ['orf'],
  'image/x-raw-minolta': ['mrw'],
  'image/x-raw-adobe': ['dng'],
  'image/x-raw-kodak': ['kdc', 'k25', 'dcr'],
  'image/x-raw-sigma': ['x3f'],
  'image/x-raw-epson': ['erf'],
  'image/x-raw-hasselblad': ['3fr'],
  'video/mp4': ['mp4', 'm4v'],
  'video/x-matroska': ['mkv'],
  'video/webm': ['webm'],
  'video/quicktime': ['mov'],
  'video/ogg': ['ogv'],
  'video/mpeg': ['vob', 'mpeg', 'mpg', 'mpe', 'm1v'],
  'video/avi': ['avi', 'divx', 'xvid'],
  'video/x-ms-wmv': ['wmv'],
  'video/x-flv': ['flv'],
  'video/3gpp': ['3gp', '3gpp'],
  'video/mp2t': ['m2ts'],
  'video/mxf': ['mxf'],
};

export const EXTENSION_MIME_TYPES: Record<string, string> = Object.fromEntries(
  Object.entries(EXTENSIONS_BY_MIME).flatMap(([mime, extensions]) =>
    extensions.map((extension) => [extension, mime]),
  ),
);

export const resolveUploadMimeType = (file: File): string => {
  const reported = file.type.toLowerCase();
  if (reported && reported !== 'application/octet-stream') {
    return MIME_TYPE_OVERRIDES[reported] ?? reported;
  }
  return EXTENSION_MIME_TYPES[file.name.slice(file.name.lastIndexOf('.') + 1).toLowerCase()] ?? reported;
};

export const enum SupportedFileType {
  Image = 'image',
  Gif = 'gif',
  Video = 'video',
}

// all details can be found here https://support.wix.com/en/article/wix-media-supported-media-file-types-and-file-sizes
export const MAX_SIZE: Record<SupportedFileType, number> = {
  [SupportedFileType.Image]: 25 * 1024 * 1024, // 25MB
  [SupportedFileType.Gif]: 15 * 1024 * 1024, // 15MB
  [SupportedFileType.Video]: 4000 * 1024 * 1024, // 4GB
};

// The Dynamics migration caps video uploads at a lower limit than the legacy media platform.
const DYNAMICS_VIDEO_MAX_SIZE = 130 * 1024 * 1024; // 130MB

export const getMaxSize = (fileType: SupportedFileType): number => {
  if (fileType === SupportedFileType.Video && experiments.is('specs.chatbot.DynamicsMigration', 'true')) {
    return DYNAMICS_VIDEO_MAX_SIZE;
  }
  return MAX_SIZE[fileType];
};
export const LIMIT_ATTACH_IMAGES = 7;
export const LEARN_MORE_ARTICLE_URL =
  'https://support.wix.com/article/wix-media-supported-media-file-types-and-file-sizes';

// https://support.wix.com/en/article/wix-media-supported-media-file-types-and-file-sizes?tabs=Videos
export const SUPPORTED_VIDEO_FILES = [
  'AVI',
  'MPEG',
  'MPG',
  'MPE',
  'MP4',
  'MKV',
  'WebM',
  'MOV',
  'OGV',
  'VOB',
  'M4V',
  '3GP',
  '3GPP',
  'DivX',
  'XVID',
  'MXF',
  'WMV',
  'M1V',
  'FLV',
  'M2TS',
];

// https://support.wix.com/en/article/wix-media-supported-media-file-types-and-file-sizes?tabs=Images
export const SUPPORTED_IMAGE_FILES = [
  'JPG',
  'PNG',
  'GIF',
  'JPEG',
  'JPE',
  'JFIF',
  'BMP',
  'HEIC',
  'HEIF',
  'TIFF',
  'TIF',
  'WEBP',
  // JPEG 2000 formats
  'jp2',
  'jpg2',
  'j2k',
  'jpf',
  'jpm',
  'j2c',
  'jpc',
  'jpx',
  // RAW formats
  'arw',
  'srw',
  'nef',
  'cr2',
  'cr3',
  'crw',
  'rwl',
  'rw2',
  'raw',
  'raf',
  'pef',
  'orf',
  'mrw',
  'dng',
  'sr2',
  'srf',
  'kdc',
  'k25',
  'dcr',
  'x3f',
  'erf',
  '3fr',
];

const supportedExtensions = new Set(
  [...SUPPORTED_VIDEO_FILES, ...SUPPORTED_IMAGE_FILES].map((e) => e.toLowerCase()),
);

export const isSupportedAttachmentFile = (file: File): boolean => {
  const dotIndex = file.name.lastIndexOf('.');
  if (dotIndex === -1) {
    return false;
  }
  return supportedExtensions.has(file.name.slice(dotIndex + 1).toLowerCase());
};

const videoExtensions = new Set(SUPPORTED_VIDEO_FILES.map((e) => e.toLowerCase()));

// Browsers don't always report file.type for videos (e.g. some .mp4 come back as ''), which would
// leave the probe unable to pick the video branch. Fall back to the file extension in that case.
export const isVideoFile = (file: File): boolean => {
  const dotIndex = file.name.lastIndexOf('.');
  if (dotIndex === -1) {
    return false;
  }
  return videoExtensions.has(file.name.slice(dotIndex + 1).toLowerCase());
};
